import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import ContentPageFooter from "@/components/ContentPageFooter";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-6 pb-24">
      <Helmet>
        <title>Privacy Policy – Ayeris</title>
        <meta name="description" content="Read the Ayeris privacy policy. Learn how we handle your data, protect your privacy, and keep your information safe." />
        <link rel="canonical" href="https://ayeris.tv/privacy-policy" />
      </Helmet>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">{t("legal.back")}</span>
      </button>

      <h1 className="text-2xl font-display font-bold mb-2">{t("legal.privacy.title")}</h1>
      <p className="text-muted-foreground text-sm mb-8">{t("legal.lastUpdated")}</p>

      <div className="space-y-6 text-sm leading-relaxed text-secondary-foreground">
        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s1Title")}</h2>
          <p>{t("legal.privacy.s1p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s2Title")}</h2>
          <p className="mb-2"><strong className="text-foreground">{t("legal.privacy.s2p1Label")}</strong> {t("legal.privacy.s2p1")}</p>
          <p className="mb-2"><strong className="text-foreground">{t("legal.privacy.s2p2Label")}</strong> {t("legal.privacy.s2p2")}</p>
          <p className="mb-2"><strong className="text-foreground">{t("legal.privacy.s2p3Label")}</strong> {t("legal.privacy.s2p3")}</p>
          <p className="mb-2"><strong className="text-foreground">{t("legal.privacy.s2p4Label")}</strong> {t("legal.privacy.s2p4")}</p>
          <p><strong className="text-foreground">{t("legal.privacy.s2p5Label")}</strong> {t("legal.privacy.s2p5")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s3Title")}</h2>
          <p className="mb-2"><strong className="text-foreground">{t("legal.privacy.s3p1Label")}</strong> {t("legal.privacy.s3p1")}</p>
          <p className="mb-2"><strong className="text-foreground">{t("legal.privacy.s3p2Label")}</strong> {t("legal.privacy.s3p2")}</p>
          <p className="mb-2"><strong className="text-foreground">{t("legal.privacy.s3p3Label")}</strong> {t("legal.privacy.s3p3")}</p>
          <p><strong className="text-foreground">{t("legal.privacy.s3p4Label")}</strong> {t("legal.privacy.s3p4")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s4Title")}</h2>
          <p className="mb-2">{t("legal.privacy.s4p1")}</p>
          <p className="mb-2"><strong className="text-foreground">{t("legal.privacy.s4p2Label")}</strong> {t("legal.privacy.s4p2")}</p>
          <p><strong className="text-foreground">{t("legal.privacy.s4p3Label")}</strong> {t("legal.privacy.s4p3")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s5Title")}</h2>
          <p className="mb-2">{t("legal.privacy.s5p1")}</p>
          <p className="mb-2">{t("legal.privacy.s5p2")}</p>
          <p>
            <strong className="text-foreground">{t("legal.privacy.s5p3Label")}</strong> {t("legal.privacy.s5p3")}{" "}
            <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              {t("legal.privacy.s5p3Link")}
            </a>.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s6Title")}</h2>
          <p className="mb-2"><strong className="text-foreground">{t("legal.privacy.s6p1Label")}</strong> {t("legal.privacy.s6p1")}</p>
          <p className="mb-2"><strong className="text-foreground">{t("legal.privacy.s6p2Label")}</strong> {t("legal.privacy.s6p2")}</p>
          <p><strong className="text-foreground">{t("legal.privacy.s6p3Label")}</strong> {t("legal.privacy.s6p3")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s7Title")}</h2>
          <p>{t("legal.privacy.s7p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s8Title")}</h2>
          <p>{t("legal.privacy.s8p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s9Title")}</h2>
          <p className="mb-2">{t("legal.privacy.s9p1")}</p>
          <ul className="list-disc list-inside space-y-1 ml-1 mb-2">
            <li>{t("legal.privacy.s9li1")}</li>
            <li>{t("legal.privacy.s9li2")}</li>
            <li>{t("legal.privacy.s9li3")}</li>
            <li>{t("legal.privacy.s9li4")}</li>
            <li>{t("legal.privacy.s9li5")}</li>
          </ul>
          <p>
            {t("legal.privacy.s9p2")}{" "}
            <a href="mailto:enquiries@ayeris.tv" className="text-primary hover:underline">enquiries@ayeris.tv</a>
          </p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s10Title")}</h2>
          <p>{t("legal.privacy.s10p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.privacy.s11Title")}</h2>
          <p>
            {t("legal.privacy.s11p1")}{" "}
            <a href="mailto:enquiries@ayeris.tv" className="text-primary hover:underline">enquiries@ayeris.tv</a>
          </p>
        </section>
      </div>

      <ContentPageFooter currentPath="/privacy-policy" />
    </div>
  );
};

export default PrivacyPolicy;
